import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Modal, Row } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/pro-solid-svg-icons'
import SelectInput from 'components/ui/forms/field/select-input/SelectInput'
import { ButtonVariant, ButtonSizes } from 'design-system/button/src/Button'
import Button from 'design-system/button'
import * as fromActions from 'store/actions/participants.action'
import getProfilesList from 'api/administration/lms-profiles/AllProfilesList'
import { LmsProfileTypes } from 'enum/lms-profile-types.enum'
import { IBasicProfile } from 'models/lmsProfile.model'
import styles from './index.module.scss'

interface IAddProfilesModal {
  show: boolean
  onClose: any
  ids: number[]
  listConfig: any
}

const AddProfilesModal: React.FC<IAddProfilesModal> = ({
  show,
  onClose,
  ids,
  listConfig,
}) => {
  const [lmsProfileList, setLmsProfileList] = useState<IBasicProfile[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedTags, setSelectedTags] = useState([])
  const { t } = useTranslation()
  const dispatch = useDispatch()

  useEffect(() => {
    setLoading(true)
    getProfilesList({
      profile_type_eq: LmsProfileTypes.participants,
    })
      .then((response) => {
        setLmsProfileList(response)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [dispatch])

  const tagsLmsProfileList = useMemo(
    () =>
      lmsProfileList.map(({ id: value, name: label, color }) => ({
        value,
        label,
        color,
      })),
    [lmsProfileList]
  )

  const handleSubmit = () => {
    const profiles = selectedTags ? selectedTags.map(({ value }) => value) : []
    dispatch({
      type: fromActions.ADD_PROFILES_BULK_ACTION,
      payload: { ids, profilesIds: profiles, listConfig },
    })
    onClose()
  }

  const prefix = 'configuration.users.addProfilesModal'
  return (
    <Modal show={show} onHide={onClose} className={styles.modalContainer}>
      <Modal.Body className={'pt-0'}>
        <Row className="position-relative">
          <h1>{t(`${prefix}.title`)}</h1>
          <a className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes}></FontAwesomeIcon>
          </a>
        </Row>
        <Row className="d-block mt-3">
          <SelectInput
            name="optionalProfiles"
            label={t(`${prefix}.subtitle`)}
            closeMenuOnSelect={true}
            options={tagsLmsProfileList}
            value={selectedTags}
            isMulti={true}
            withColors={true}
            onChange={(selected: any) => setSelectedTags(selected || [])}
            cleareable={false}
            error={''}
            loadingMessage={() => t(`${prefix}.loading`)}
            isLoading={loading}
          />
        </Row>
        <Row>
          <p className={`${styles.legend} mt-2`}>
            <Trans
              i18nKey={`${prefix}.legend`}
              values={{
                text: t(
                  `${prefix}.participant${ids.length !== 1 ? '_plural' : ''}`,
                  { count: ids.length }
                ),
              }}
            />
          </p>
        </Row>
        <Row className={styles.footer}>
          <Button
            variant={ButtonVariant.Secondary}
            size={ButtonSizes.Small}
            onClick={onClose}
          >
            {t(`${prefix}.buttons.cancel`)}
          </Button>
          <Button
            className="ml-2 mr-3 "
            disabled={!selectedTags.length}
            variant={ButtonVariant.Primary}
            size={ButtonSizes.Small}
            onClick={() =>
              selectedTags && selectedTags.length && handleSubmit()
            }
          >
            {t(`${prefix}.buttons.accept`)}
          </Button>
        </Row>
      </Modal.Body>
    </Modal>
  )
}

export default AddProfilesModal
